import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// import { Button } from '@/components/ui/button';
import { TimeFormat, UserFormData, UserRole } from '@/types/user.types'; // User
import { userService } from '@/services/api/services/userService';
import { useTranslation } from 'react-i18next';
import { PasswordStrength } from '@/components/ui/password-strength';
import { Eye, EyeOff, Lock } from 'lucide-react';
// import { UserX } from 'lucide-react'; // Temporarily commented out - block user functionality
import { PhoneInputCustom } from './PhoneInputCustom';
import { PasswordResetButton } from './PasswordResetButton';
import { ManualPasswordResetModal } from './ManualPasswordResetModal';
import { userMappers } from '@/types/mappers/userMappers';
import { validateEmail } from '@/services/validator/validate.email';
import { useToast } from '@/contexts/ToastContext';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { usePermissions } from '@/contexts/PermissionsContext';
import { PERMISSIONS } from '@/contexts/PermissionsContext';
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation';
import { useAuth } from '@/contexts/AuthContext';

export const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { hasPermission } = usePermissions();
  const { confirmDelete, showErrorDialog } = useDeleteConfirmation();
  const { user } = useAuth();

  // Helper function to check if we're in a development environment
  const isDevelopment = () => {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname.startsWith('dev.pring') ||
      hostname.startsWith('staging.pring')
    );
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isManualResetModalOpen, setIsManualResetModalOpen] = useState(false);

  const [formData, setFormData] = useState<UserFormData>({
    id: '',
    personalInfo: {
      name: '',
      surname1: '',
      email1: '',
      phone1: '',
      company: '',
    },
    credentials: {
      username: '',
      passwordLastChanged: new Date(),
      requiresPasswordChange: true,
    },
    role: UserRole.USER,
    timezone: 'America/Argentina/Buenos_Aires',
    preferences: {
      dateFormat: 'DD/MM/YYYY',
      timeFormat: TimeFormat.HOURS_24,
      timezone: 'America/Argentina/Buenos_Aires',
    },
    metadata: {
      version: 1,
      subscription: 'active',
      subscriptionExpiryDate: undefined,
    },
  });

  const [originalFormData, setOriginalFormData] = useState<UserFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [isSendingReset, setIsSendingReset] = useState(false);
  // const [isBlockingUser, setIsBlockingUser] = useState(false); // Temporarily commented out - block user functionality
  const [isManualResetting, setIsManualResetting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  // Security validation function
  const validateSecurityAccess = useCallback(() => {
    if (!user?.id) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      navigate('/');
      return;
    }

    // Only Administrators can access user management
    if (user.role !== 'Administrador') {
      showToast(t('errors.noAccessToUserManagement'), 'error');
      navigate('/');
      return;
    }

    setIsCheckingSecurity(false);
  }, [user, t, showToast, navigate]);

  // Function to check if there are actual changes in the form
  const hasChanges = useCallback((): boolean => {
    if (!originalFormData) return false;

    // Normalize dates for comparison
    const normalizeForComparison = (data: UserFormData) => {
      const normalized = { ...data };

      // Normalize date fields
      if (normalized.credentials.passwordLastChanged) {
        normalized.credentials.passwordLastChanged = new Date(
          normalized.credentials.passwordLastChanged
        );
      }
      if (normalized.metadata?.subscriptionExpiryDate) {
        normalized.metadata.subscriptionExpiryDate = new Date(
          normalized.metadata.subscriptionExpiryDate
        );
      }

      return normalized;
    };

    const currentNormalized = normalizeForComparison(formData);
    const originalNormalized = normalizeForComparison(originalFormData);

    // Deep comparison of form data
    const currentData = JSON.stringify(currentNormalized);
    const originalData = JSON.stringify(originalNormalized);

    return currentData !== originalData;
  }, [formData, originalFormData]);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setFormError(null);
      const response = await userService.getById(id);
      if (response.success) {
        const userFormData = userMappers.toFormData(response.data);
        setFormData(userFormData);
        setOriginalFormData(userFormData); // Store original data for comparison
        setIsDirty(false); // Reset dirty state when loading user data
      } else {
        setFormError(response.error.message || t('errors.fetchUserFailed'));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setFormError(t('errors.fetchUserFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  // Security validation effect
  useEffect(() => {
    if (user?.id) {
      validateSecurityAccess();
    }
  }, [user?.id, validateSecurityAccess]);

  useEffect(() => {
    // Only fetch user data if security check passed and we're in edit mode
    if (!isCheckingSecurity && id && user?.role === 'Administrador') {
      fetchUser();
    }
  }, [isCheckingSecurity, id, user?.role, fetchUser]);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (isDevelopment()) {
      console.log('🔍 Validating form with data:', {
        name: formData.personalInfo.name,
        surname1: formData.personalInfo.surname1,
        email1: formData.personalInfo.email1,
        timezone: formData.timezone,
        id: id,
        password: formData.credentials.password,
        confirmPassword: confirmPassword,
      });
    }

    // Required fields validation
    if (!formData.personalInfo.name) {
      errors.name = t('validation.required');
      if (isDevelopment()) console.log('❌ Name is required');
    }
    if (!formData.personalInfo.surname1) {
      errors.surname1 = t('validation.required');
      if (isDevelopment()) console.log('❌ Surname is required');
    }
    if (!formData.personalInfo.email1) {
      errors.email1 = t('validation.required');
      if (isDevelopment()) console.log('❌ Email is required');
    }
    // No password required on create
    if (id && !formData.credentials.password) {
      // Only when editing and trying to change password
    }
    if (!formData.timezone) {
      errors.timezone = t('validation.required');
      if (isDevelopment()) console.log('❌ Timezone is required');
    }

    // Validate emails
    if (formData.personalInfo.email1 && !validateEmail(formData.personalInfo.email1)) {
      errors.email1 = t('validation.invalidEmail');
      if (isDevelopment()) console.log('❌ Email format is invalid');
    }

    // Validate password when editing
    if (id) {
      if (formData.credentials.password && formData.credentials.password !== confirmPassword) {
        errors.confirmPassword = t('validation.passwordMismatch');
        if (isDevelopment()) console.log('❌ Password mismatch');
      }
    }

    if (isDevelopment()) {
      console.log('🔍 Validation errors:', errors);
      console.log('🔍 Form is valid:', Object.keys(errors).length === 0);
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, id, confirmPassword, t]);

  // Clean the form data by removing empty strings from optional fields
  const cleanFormData = (data: UserFormData): UserFormData => {
    const cleaned = { ...data };
    const { personalInfo } = cleaned;

    // Create a new personalInfo object with only non-empty values
    cleaned.personalInfo = {
      name: personalInfo.name,
      surname1: personalInfo.surname1,
      ...(personalInfo.company && { company: personalInfo.company }),
      ...(personalInfo.email1 && { email1: personalInfo.email1 }),
      ...(personalInfo.phone1 && { phone1: personalInfo.phone1 }),
    };

    // Set username to email value
    cleaned.credentials.username = personalInfo.email1 || '';

    // Ensure requiresPasswordChange true when creating without password
    if (!id && !cleaned.credentials.password) {
      cleaned.credentials.requiresPasswordChange = true;
    }

    // Ensure metadata has default values
    cleaned.metadata = {
      version: data.metadata?.version || 1,
      subscription: data.metadata?.subscription || 'active',
      subscriptionExpiryDate: data.metadata?.subscriptionExpiryDate,
    };

    return cleaned;
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [formData, confirmPassword, validateForm, hasChanges]);

  // Debug useEffect to see what's happening with hasChanges
  useEffect(() => {
    if (isDevelopment()) {
      console.log('Debug - hasChanges():', hasChanges());
      console.log('Debug - originalFormData:', originalFormData);
      console.log('Debug - formData:', formData);
      console.log('Debug - isFormValid:', isFormValid);
    }
  }, [hasChanges, originalFormData, formData, isFormValid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);
    setFormError(null);

    try {
      const cleanedFormData: UserFormData = cleanFormData(formData);

      if (id) {
        // First update the user data
        const updateResponse = await userService.update(id, cleanedFormData);

        if (updateResponse.success) {
          // If there's a password and it's changed, update it separately
          if (formData.credentials.password) {
            const passwordResponse = await userService.updatePassword(id, {
              newPassword: formData.credentials.password,
            });
            if (!passwordResponse.success) {
              setFormError(passwordResponse.error.message || t('errors.updatePasswordFailed'));
              return;
            }
          }
          navigate('/users');
        } else {
          setFormError(updateResponse.error.message || t('errors.updateUserFailed'));
        }
      } else {
        // For new users, do not require password
        const createResponse = await userService.create(cleanedFormData);
        if (createResponse.success) {
          navigate('/users');
        } else {
          setFormError(t(`service.errors.${createResponse.error.code}`));
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setFormError(t('errors.saveUserFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // TODO: Remove this once we have a proper type for the event
  // eslint-disable-next-line no-undef
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // TODO: Remove this once we have a proper type for the object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateNestedState = (obj: any, path: string[], value: any): any => {
      const [current, ...rest] = path;
      if (rest.length === 0) {
        return {
          ...obj,
          // TODO: Remove this once we have a proper type for the object
          // eslint-disable-next-line no-undef
          [current]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        };
      }
      return {
        ...obj,
        [current]: updateNestedState(obj[current] || {}, rest, value),
      };
    };

    const path = name.split('.');
    setFormData((prev) => updateNestedState(prev, path, value));
  };

  const handleSendPasswordReset = async () => {
    if (!id || !formData.personalInfo.email1) return;

    setIsSendingReset(true);
    setFormError(null);

    try {
      const response = await userService.sendEmailPasswordReset(id);
      if (response.success) {
        showToast(t('pages.users.passwordResetSent'), 'success');
      } else {
        setFormError(response.error.message || t('errors.passwordResetFailed'));
      }
    } catch (error: unknown) {
      console.error('Error sending password reset:', error);
      // Log adicional para debug
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: unknown } };
        console.log('Status:', axiosError.response?.status);
        console.log('Data:', axiosError.response?.data);
      }
      setFormError(t('errors.passwordResetFailed'));
    } finally {
      setIsSendingReset(false);
    }
  };

  // Temporarily commented out - block user functionality
  /*
  const handleBlockUser = async () => {
    if (!id) return;

    showConfirmation({
      title: t('pages.users.modals.blockUser.title'),
      message: t('pages.users.modals.blockUser.message', { name: formData.personalInfo.name }),
      confirmText: t('pages.users.modals.blockUser.confirm'),
      cancelText: t('common.cancel'),
      type: 'danger',
      onConfirm: async () => {
        setIsBlockingUser(true);
        setFormError(null);

        try {
          const response = await userService.blockUser(id);
          if (response.success) {
            showToast(t('pages.users.userBlocked'), 'success');
            navigate('/users');
          } else {
            setFormError(response.error.message || t('errors.blockUserFailed'));
          }
        } catch (error) {
          console.error('Error blocking user:', error);
          setFormError(t('errors.blockUserFailed'));
        } finally {
          setIsBlockingUser(false);
        }
      },
    });
  };
  */

  const handleManualPasswordReset = async (newPassword: string) => {
    if (!id) return;

    setIsManualResetting(true);
    setFormError(null);

    try {
      const response = await userService.manualPasswordReset(id, newPassword);
      if (response.success) {
        showToast(t('pages.users.passwordResetManualSuccess'), 'success');
      } else {
        setFormError(response.error.message || t('errors.manualPasswordResetFailed'));
      }
    } catch (error) {
      console.error('Error manual password reset:', error);
      setFormError(t('errors.manualPasswordResetFailed'));
    } finally {
      setIsManualResetting(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!id) return;

    const userName = formData.credentials?.username || formData.personalInfo.name || id;

    confirmDelete(userName, 'user', false, async () => {
      setIsDeleting(true);
      setFormError(null);

      try {
        const response = await userService.delete(id);
        if (response.success) {
          showToast(t('common.userPermanentlyDeletedSuccess', { name: userName }), 'success');
          navigate('/users');
        } else {
          // Handle specific error cases
          if (response.error.code === 'USER_HAS_ASSOCIATIONS') {
            showErrorDialog(userName, 'user', response.error.code);
          } else {
            setFormError(
              response.error.message || t('common.userPermanentDeleteError', { name: userName })
            );
          }
        }
      } catch (error) {
        console.error('Error permanently deleting user:', error);
        setFormError(t('common.userPermanentDeleteError', { name: userName }));
      } finally {
        setIsDeleting(false);
      }
    });
  };

  const enablePasswordFields = false;

  // Show loading while checking security or loading user data
  if (isCheckingSecurity || (isLoading && id)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-white p-8">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-brand-orange"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              {id ? t('common.saving') : t('common.creating')}
            </h3>
            <p className="text-sm text-gray-600 text-center">
              {id ? t('pages.users.savingUser') : t('pages.users.creatingUser')}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-brand-black mb-6 sentence-case">
          {id ? t('pages.users.editUser') : t('pages.users.createNew')}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Info Section */}
          <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
            <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
              {t('pages.users.sections.personalInfo')}
            </h2>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.users.fields.name')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="personalInfo.name"
                    value={formData.personalInfo.name}
                    onChange={handleChange}
                    required
                    placeholder={t('pages.users.placeholders.name')}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.users.fields.surname')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="personalInfo.surname1"
                    value={formData.personalInfo.surname1}
                    onChange={handleChange}
                    required
                    placeholder={t('pages.users.placeholders.surname')}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.users.fields.company')}
                  </label>
                  <input
                    type="text"
                    name="personalInfo.company"
                    value={formData.personalInfo.company}
                    onChange={handleChange}
                    placeholder={t('pages.users.placeholders.company')}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.users.fields.phone1')}
                  </label>
                  <PhoneInputCustom
                    value={formData.personalInfo.phone1 || ''}
                    onChange={(val) => {
                      setFormData((prev) => ({
                        ...prev,
                        personalInfo: {
                          ...prev.personalInfo,
                          phone1: val,
                        },
                      }));
                    }}
                    name="personalInfo.phone1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Credentials Section */}
          <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
            <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
              {t('pages.users.sections.credentials')}
            </h2>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.users.fields.email1')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="email"
                  pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
                  name="personalInfo.email1"
                  value={formData.personalInfo.email1 || ''}
                  onChange={handleChange}
                  disabled={!!id}
                  required
                  placeholder={t('pages.users.placeholders.email1')}
                  className={`w-full ${id ? 'special-input' : ''}`}
                />
                {validationErrors.email1 && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email1}</p>
                )}
              </div>
              {/* Username field is hidden - it's automatically set from email */}
              <input
                type="hidden"
                name="credentials.username"
                value={formData.personalInfo.email1 || ''}
              />

              {/* Password fields - hide for new users */}
              {id && enablePasswordFields && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-brand-gray-dark">
                      {t('pages.users.fields.password')}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="credentials.password"
                        value={formData.credentials.password}
                        onChange={handleChange}
                        placeholder={t('pages.users.placeholders.password')}
                        className="w-full pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {formData.credentials.password && (
                      <PasswordStrength password={formData.credentials.password} />
                    )}
                    {validationErrors.password && isDirty && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-gray-dark">
                      {t('pages.users.fields.confirmPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                        }}
                        placeholder={t('pages.users.placeholders.confirmPassword')}
                        className="w-full pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {validationErrors.confirmPassword && isDirty && (
                      <p className="mt-1 text-sm text-red-600">
                        {validationErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="flex items-center">
                {id && (
                  <>
                    <input
                      type="checkbox"
                      name="credentials.requiresPasswordChange"
                      checked={formData.credentials.requiresPasswordChange}
                      onChange={handleChange}
                      className="h-4 w-4 text-brand-orange focus:ring-brand-orange border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-brand-gray-dark">
                      {t('pages.users.fields.requirePasswordChange')}
                    </label>
                  </>
                )}
              </div>

              {/* Action buttons for edit mode */}
              {id && formData.personalInfo.email1 && (
                <div className="flex justify-end gap-2 pt-2">
                  <PasswordResetButton
                    onClick={handleSendPasswordReset}
                    disabled={isSendingReset}
                    isLoading={isSendingReset}
                    label={t('pages.users.sendPasswordReset')}
                    loadingLabel={t('pages.users.sendingReset')}
                  />
                  <button
                    type="button"
                    onClick={() => setIsManualResetModalOpen(true)}
                    disabled={isManualResetting}
                    className="flex items-center gap-2 bg-brand-orange text-white px-3 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Lock className="h-4 w-4" />
                    {isManualResetting ? t('common.saving') : t('pages.users.manualPasswordReset')}
                  </button>
                  {/* Temporarily commented out - block user functionality
                  <button
                    type="button"
                    onClick={handleBlockUser}
                    disabled={isBlockingUser}
                    className="flex items-center gap-2 bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors duration-200 sentence-case disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UserX className="h-4 w-4" />
                    {isBlockingUser ? t('common.saving') : t('pages.users.blockUser')}
                  </button>
                  */}
                </div>
              )}
            </div>
          </div>

          {/* Role Section */}
          <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
            <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
              {t('pages.users.sections.role')}
            </h2>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text_sm font-medium text-brand-gray-dark">
                  {t('pages.users.fields.role')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    handleChange(e);
                    // Set default subscription values for Administrador
                    if (newRole === 'Administrador') {
                      const futureDate = new Date();
                      futureDate.setFullYear(2500);
                      setFormData((prev) => ({
                        ...prev,
                        metadata: {
                          ...prev.metadata,
                          subscription: 'active',
                          subscriptionExpiryDate: futureDate,
                        },
                      }));
                    }
                  }}
                  required
                  className="w-full rounded-md border-2 border-gray-300 shadow-sm bg-gray-10 focus:border-brand-orange focus:ring-brand-orange"
                >
                  <option value="Administrador">{t('pages.users.roles.administrador')}</option>
                  <option value="Supervisor">{t('pages.users.roles.supervisor')}</option>
                  <option value="Operario">{t('pages.users.roles.operario')}</option>
                </select>
                <p className="mt-2 text-sm text-brand-gray-light">
                  {formData.role === 'Supervisor' && t('pages.users.roleDescriptions.supervisor')}
                  {formData.role === 'Operario' && t('pages.users.roleDescriptions.operario')}
                </p>
              </div>
            </div>
          </div>

          {/* Timezone and Preferences Section */}
          <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
            <div className="bg-brand-gray-dark p-4 rounded-t-lg">
              <h2 className="text-xl font-semibold text-brand-white">
                {t('pages.users.sections.preferences')}
              </h2>
              <p className="text-sm text-brand-gray-light italic mt-1">
                {t('pages.users.sections.preferencesNote.description')}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.users.fields.timezone')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border-2 border-gray-300 shadow-sm bg-gray-10 focus:border-brand-orange focus:ring-brand-orange"
                >
                  {Object.entries(t('pages.users.timezones', { returnObjects: true })).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label as string}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.users.fields.dateFormat')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="preferences.dateFormat"
                  value={formData.preferences.dateFormat}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border-2 border-gray-300 shadow-sm bg-gray-10 focus:border-brand-orange focus:ring-brand-orange"
                >
                  {Object.entries(t('pages.users.dateFormats', { returnObjects: true })).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label as string}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.users.fields.timeFormat')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="preferences.timeFormat"
                  value={formData.preferences.timeFormat}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border-2 border-gray-300 shadow-sm bg-gray-10 focus:border-brand-orange focus:ring-brand-orange"
                >
                  <option value="12h">12h</option>
                  <option value="24h">24h</option>
                </select>
              </div>
            </div>
          </div>

          {/* Subscription Section - Hidden for Administrators */}
          {formData.role !== 'Administrador' && (
            <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
              <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
                {t('pages.users.sections.subscription')}
              </h2>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.users.fields.subscription')}
                  </label>
                  <select
                    name="metadata.subscription"
                    value={formData.metadata?.subscription || 'active'}
                    onChange={handleChange}
                    className="w-full rounded-md border-2 border-gray-300 shadow-sm bg-gray-10 focus:border-brand-orange focus:ring-brand-orange"
                  >
                    <option value="active">{t('pages.users.subscription.active')}</option>
                    <option value="inactive">{t('pages.users.subscription.inactive')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.users.fields.subscriptionExpiryDate')}
                  </label>
                  <DateTimePicker
                    value={
                      formData.metadata?.subscriptionExpiryDate
                        ? new Date(formData.metadata.subscriptionExpiryDate)
                        : null
                    }
                    onChange={(date) => {
                      setFormData((prev) => ({
                        ...prev,
                        metadata: {
                          ...prev.metadata,
                          subscriptionExpiryDate: date || undefined,
                        },
                      }));
                    }}
                    placeholder={t('pages.users.placeholders.subscriptionExpiryDate')}
                    showTimeSelect={false}
                    dateFormat="dd/MM/yyyy"
                    // minDate={new Date()}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            {/* Permanent Delete Button - Only for Administrators in Edit Mode */}
            {id && hasPermission(PERMISSIONS.USER_DELETE) && (
              <button
                type="button"
                onClick={handlePermanentDelete}
                disabled={isDeleting || isLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200 sentence-case disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? t('common.saving') : t('common.permanentDelete')}
              </button>
            )}

            {/* Save and Cancel Buttons */}
            <div className="flex gap-4 ml-auto">
              <button
                type="button"
                onClick={() => navigate('/users')}
                className="bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200 sentence-case"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={(() => {
                  // In creation mode, only check if form is valid
                  // In edit mode, check if form is valid AND there are changes
                  const disabled = id
                    ? isLoading || !isFormValid || !hasChanges()
                    : isLoading || !isFormValid;

                  if (isDevelopment()) {
                    console.log('Save button disabled:', disabled, {
                      isLoading,
                      isFormValid,
                      hasChanges: hasChanges(),
                      isEditMode: !!id,
                    });
                  }
                  return disabled;
                })()}
                className={`bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 sentence-case ${
                  id
                    ? isLoading || !isFormValid || !hasChanges()
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                    : isLoading || !isFormValid
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                }`}
              >
                {isLoading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </form>

        {/* Manual Password Reset Modal */}
        <ManualPasswordResetModal
          isOpen={isManualResetModalOpen}
          onClose={() => setIsManualResetModalOpen(false)}
          onConfirm={handleManualPasswordReset}
          isLoading={isManualResetting}
        />

        {formError && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
            <h3 className="font-semibold text-red-900 mb-2">Error</h3>
            <p className="text-red-700">{formError}</p>
          </div>
        )}
      </div>
    </div>
  );
};
